import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { settingsService } from '../../services/settingsService';
import { Switch } from '../ui/custom/Switch';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

export default function TicketTitleSuggestionsSettings() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [enabled, setEnabled] = useState(false);
  const [autoTitleEnabled, setAutoTitleEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingAutoTitle, setSavingAutoTitle] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const isEnabled = await settingsService.getTicketTitleSuggestionsEnabled();
        setEnabled(isEnabled);

        const isAutoTitleEnabled = await settingsService.getAutoTitleEnabled();
        setAutoTitleEnabled(isAutoTitleEnabled);
      } catch (error) {
        console.error('Error loading settings:', error);
        toast.error(t('settings.ticketTitleSuggestions.settingsError'));
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [t]);

  const handleToggle = async (newValue: boolean) => {
    try {
      setSaving(true);
      const success = await settingsService.setTicketTitleSuggestionsEnabled(newValue);
      if (success) {
        setEnabled(newValue);
        toast.success(t('settings.ticketTitleSuggestions.settingsSaved'));
      } else {
        toast.error(t('settings.ticketTitleSuggestions.settingsError'));
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(t('settings.ticketTitleSuggestions.settingsError'));
    } finally {
      setSaving(false);
    }
  };

  const handleAutoTitleToggle = async (newValue: boolean) => {
    try {
      setSavingAutoTitle(true);
      const success = await settingsService.setAutoTitleEnabled(newValue);
      if (success) {
        setAutoTitleEnabled(newValue);
        toast.success(t('settings.ticketTitleSuggestions.settingsSaved'));
      } else {
        toast.error(t('settings.ticketTitleSuggestions.settingsError'));
      }
    } catch (error) {
      console.error('Error saving auto-title setting:', error);
      toast.error(t('settings.ticketTitleSuggestions.settingsError'));
    } finally {
      setSavingAutoTitle(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div
          className={`animate-spin rounded-full h-8 w-8 border-b-2 ${
            isDark ? 'border-blue-400' : 'border-blue-500'
          }`}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {t('settings.ticketTitleSuggestions.title')}
        </h2>
        <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {t('settings.ticketTitleSuggestions.description')}
        </p>
      </div>

      {/* Enable/Disable Toggle */}
      <div
        className={`p-4 rounded-lg border ${
          isDark ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
        }`}
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className={`text-base font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('settings.ticketTitleSuggestions.enableFeature')}
            </h3>
            <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('settings.ticketTitleSuggestions.enableDescription')}
            </p>
          </div>
          <Switch checked={enabled} onCheckedChange={handleToggle} disabled={saving} />
        </div>
      </div>

      {/* Auto-Generate Titles Toggle */}
      {enabled && (
        <div
          className={`p-4 rounded-lg border ${
            isDark ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className={`text-base font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('settings.ticketTitleSuggestions.autoTitleToggle', 'Auto-Generate Titles')}
              </h3>
              <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {t('settings.ticketTitleSuggestions.autoTitleDescription', 'Automatically fill ticket title based on selected tags')}
              </p>
            </div>
            <Switch
              checked={autoTitleEnabled}
              onCheckedChange={handleAutoTitleToggle}
              disabled={savingAutoTitle}
            />
          </div>
        </div>
      )}

      {/* Manage Suggestions Link */}
      <div
        className={`p-4 rounded-lg border ${
          isDark ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
        }`}
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className={`text-base font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('settings.ticketTitleSuggestions.manageSuggestions')}
            </h3>
            <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('settings.ticketTitleSuggestions.manageSuggestionsDescription')}
            </p>
          </div>
          <Link
            to="/settings/tags"
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              isDark
                ? 'bg-indigo-600 text-white hover:bg-indigo-500'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            }`}
          >
            {t('settings.tags')}
          </Link>
        </div>
      </div>
    </div>
  );
}
